/**
 * GET /api/v1/learning (v0.1.27): the Leadler > Öğrenme view model. The shape is the panel's
 * design model (apps/panel/src/api/learning.ts, ten blocks). The API fills what the database
 * knows today (switches, scan cells, search health, stage runs) and sends "no estimate"
 * (value null, n below minN) for everything the learning loop has not produced yet; the panel
 * writes "Tahmin yok" for those. Company level only: no buyer data, no contact fields.
 * Instants are epoch milliseconds, money is micro USD.
 */
import { z } from 'zod';
import { rejectReasonCodeSchema } from '../ids.js';
const instant = z.number().int().nonnegative();
const count = z.number().int().nonnegative();
export const INTERVAL_METHODS = ['wilson', 'beta_posterior', 'bootstrap', 'chapman', 'none'];
/** A number with its sample size and interval; value null below minN. */
export const learningEstimateSchema = z.object({
    value: z.number().nullable(),
    n: count,
    minN: count,
    low: z.number().nullable(),
    high: z.number().nullable(),
    level: z.number().min(0).max(1),
    method: z.enum(INTERVAL_METHODS),
});
/** "Tahmin yok": nothing measured yet, or too little. */
export function noEstimate(minN, method = 'none', n = 0) {
    return { value: null, n, minN, low: null, high: null, level: 0.95, method };
}
/** Wilson interval of k successes in n trials, or no estimate below minN. */
export function shareEstimate(k, n, minN, level = 0.95) {
    if (n < minN || n <= 0)
        return noEstimate(minN, 'wilson', n);
    const z = level === 0.9 ? 1.645 : 1.96;
    const p = k / n;
    const denominator = 1 + (z * z) / n;
    const centre = (p + (z * z) / (2 * n)) / denominator;
    const margin = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / denominator;
    const round = (value) => Math.round(value * 10_000) / 10_000;
    return {
        value: round(p),
        n,
        minN,
        low: round(Math.max(0, centre - margin)),
        high: round(Math.min(1, centre + margin)),
        level,
        method: 'wilson',
    };
}
// ---- 1. scorecard -----------------------------------------------------------
export const SCORECARD_IDS = [
    'qualified_per_10_queries',
    'cost_per_qualified',
    'cost_per_kabul',
    'fit_auc',
    'audit_field_error',
    'must_not_deliver',
    'regression',
    'positive_replies_per_100',
    'edit_rate',
];
export const METRIC_UNITS = ['share', 'per10', 'per100', 'usd', 'auc', 'count', 'fraction'];
export const scorecardTileSchema = z.object({
    id: z.enum(SCORECARD_IDS),
    label: z.string().min(1),
    unit: z.enum(METRIC_UNITS),
    estimate: learningEstimateSchema,
    /** The 4 weeks before, oldest first. */
    trend: z.array(z.number().nullable()).length(4),
    better: z.enum(['higher', 'lower']),
});
/** The nine tiles in panel order with their Turkish labels, units and direction. */
export const SCORECARD_TILES = Object.freeze([
    { id: 'qualified_per_10_queries', label: '10 sorguda nitelikli', unit: 'per10', better: 'higher' },
    { id: 'cost_per_qualified', label: 'Nitelikli başı maliyet', unit: 'usd', better: 'lower' },
    { id: 'cost_per_kabul', label: 'Doğrulanan başı maliyet', unit: 'usd', better: 'lower' },
    { id: 'fit_auc', label: 'Uyum AUC (yeni etiketler)', unit: 'auc', better: 'higher' },
    { id: 'audit_field_error', label: 'Denetim alan hatası', unit: 'share', better: 'lower' },
    { id: 'must_not_deliver', label: 'Teslim edilmemeli', unit: 'count', better: 'lower' },
    { id: 'regression', label: 'Regresyon N/N', unit: 'fraction', better: 'higher' },
    { id: 'positive_replies_per_100', label: '100 teslimde olumlu cevap', unit: 'per100', better: 'higher' },
    { id: 'edit_rate', label: 'Düzeltme oranın', unit: 'share', better: 'lower' },
]);
/** Sample sizes below which a tile shows no estimate (design section 4). */
export const SCORECARD_MIN_N = Object.freeze({
    qualified_per_10_queries: 20,
    cost_per_qualified: 10,
    cost_per_kabul: 10,
    fit_auc: 30,
    audit_field_error: 20,
    must_not_deliver: 20,
    regression: 1,
    positive_replies_per_100: 50,
    edit_rate: 10,
});
// ---- 2. changes and 3. batch -----------------------------------------------------
export const LEARNING_LOOPS = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7'];
export const LEARNING_OBJECT_TYPES = [
    'suppression',
    'opt_out_phrase',
    'sequence',
    'mailbox',
    'mail_path',
    'gateway_ok',
    'query_run',
    'arm_allocation',
    'arm_prune',
    'cell_order',
    'saturation',
    'template_weight',
    'template_locality',
    'verify_queue_order',
    'campaign_order',
];
export const learningChangeSchema = z.object({
    changeId: z.string().min(1),
    loop: z.enum(LEARNING_LOOPS),
    changeClass: z.enum(['A1', 'A2']),
    objectType: z.enum(LEARNING_OBJECT_TYPES),
    objectRef: z.string(),
    objectLabel: z.string(),
    before: z.string(),
    after: z.string(),
    why: z.string(),
    evidence: learningEstimateSchema.nullable(),
    appliedAt: instant,
    appliedBy: z.string(),
    revertedAt: instant.nullable(),
    revertedBy: z.string().nullable(),
    reversible: z.boolean(),
});
export const TERM_SLOTS = ['business_type', 'product', 'vehicle', 'modifier'];
export const keywordEvidenceSchema = z.object({
    term: z.string(),
    slot: z.enum(TERM_SLOTS),
    locale: z.string(),
    region: z.string(),
    lift: z.number(),
    dfQ: count,
    nQ: count,
    dfN: count,
    nN: count,
    businessCategories: count,
    vehicleMapped: z.boolean().nullable(),
    exampleDomains: z.array(z.string()),
    biasNote: z.string().nullable(),
});
export const learningProposalSchema = z.object({
    proposalId: z.string().min(1),
    actionType: z.string().regex(/^[a-z][a-z0-9_]*$/),
    changeClass: z.enum(['H1', 'H2', 'H3']),
    loop: z.enum(LEARNING_LOOPS),
    title: z.string(),
    what: z.string(),
    evidenceText: z.string(),
    evidence: learningEstimateSchema.nullable(),
    expectedEffect: z.string(),
    risk: z.string(),
    reversible: z.boolean(),
    judgedBy: z.string(),
    keyword: keywordEvidenceSchema.nullable(),
    releasePr: z.string().nullable(),
});
export const learningBatchSchema = z.object({
    batchId: z.string().min(1),
    kind: z.literal('learning_batch'),
    title: z.string(),
    week: z.string(),
    createdAt: instant,
    expiresAt: instant,
    targetMinutes: count,
    estimatedMinutes: count,
    payloadHash: z.string(),
    proposals: z.array(learningProposalSchema),
});
// ---- 4. arms and 5. vocabulary -------------------------------------------------
export const ARM_STATUSES = ['exploring', 'proven', 'pruned', 'downweighted'];
export const TERM_ORIGINS = ['seed', 'pilot', 'learned'];
export const LOCALITY_KINDS = ['suburb', 'industrial_estate', 'postcode'];
export const armRowSchema = z.object({
    armKey: z.string(),
    locale: z.string(),
    region: z.string(),
    /** The search source that ran the arm: serper, brave, places ... (growth.query_runs.provider). */
    source: z.string().regex(/^[a-z][a-z0-9_-]{1,31}$/),
    template: z.string(),
    terms: z.array(z.string()),
    termOrigin: z.enum(TERM_ORIGINS),
    localityKind: z.enum(LOCALITY_KINDS),
    runs: count,
    successes: count,
    posterior: learningEstimateSchema,
    status: z.enum(ARM_STATUSES),
    lastRunAt: instant.nullable(),
    costPerSuccess: z.number().int().nonnegative().nullable(),
});
export const TERM_STATUSES = ['candidate', 'active', 'retired'];
export const keywordTermRowSchema = z.object({
    termId: z.string(),
    locale: z.string(),
    slot: z.enum(TERM_SLOTS),
    term: z.string(),
    status: z.enum(TERM_STATUSES),
    origin: z.enum(TERM_ORIGINS),
    region: z.string().nullable(),
    lift: z.number().nullable(),
    yield: learningEstimateSchema,
    history: z.array(z.object({ at: instant, status: z.enum(TERM_STATUSES), note: z.string() })),
});
export const vocabularyLocaleSchema = z.object({
    locale: z.string(),
    version: count,
    hash: z.string(),
    active: count,
    candidate: count,
    retired: count,
    learnedYield: learningEstimateSchema,
    seedYield: learningEstimateSchema,
    terms: z.array(keywordTermRowSchema),
});
// ---- 6. cells -----------------------------------------------------------------
export const saturationCurveSchema = z.object({
    cellId: z.string(),
    region: z.string(),
    points: z.array(z.object({ queries: count, newQualified: count })),
    satK: count,
    saturated: z.boolean(),
});
export const frontierRowSchema = z.object({
    cellId: z.string(),
    region: z.string(),
    rank: z.number().int().positive(),
    score: z.number(),
    reason: z.string(),
    components: z.object({
        neighbourYield: z.number(),
        prior: z.number(),
        shrinkK: z.number(),
        neighbourQualified: z.number(),
        neighbourQueries: z.number(),
        demand: z.number(),
        anchor: z.number(),
        ringDistance: z.number(),
    }),
    holdout: z.boolean(),
});
export const regionEstimateSchema = z.object({
    region: z.string(),
    asOf: instant,
    method: z.enum(['chapman', 'lincoln_petersen']),
    n1: count,
    n2: count,
    m: count,
    estimate: z.number().nullable(),
    ciLow: z.number().nullable(),
    ciHigh: z.number().nullable(),
    reportable: z.boolean(),
});
export const cellLearningSchema = z.object({
    saturation: z.array(saturationCurveSchema),
    frontier: z.array(frontierRowSchema),
    holdoutYield: learningEstimateSchema,
    frontierYield: learningEstimateSchema,
    regionEstimates: z.array(regionEstimateSchema),
    sectors: z.array(z.object({
        region: z.string(),
        sector: z.string(),
        judgedCells: count,
        status: z.enum(['open', 'stopped', 'proposed_stop']),
    })),
});
// ---- 7. fit, 8. verification, 9. mail, 10. health ----------------------------------
export const FIT_TIERS = ['A', 'B', 'C', 'D'];
export const FIT_LABELS = ['high', 'medium', 'low', 'unfit'];
export const LABEL_SOURCES = ['sercan', 'audit', 'reverify', 'reply', 'deal'];
export const fitReviewSchema = z.object({
    reportOnly: z.literal(true),
    formulaDecisionPending: z.boolean(),
    tierOutcome: z.array(z.object({
        tier: z.enum(FIT_TIERS),
        counts: z.object({ high: count, medium: count, low: count, unfit: count }),
        good: learningEstimateSchema,
    })),
    components: z.array(z.object({ component: z.string(), association: learningEstimateSchema })),
    versions: z.array(z.object({
        version: z.string(),
        status: z.enum(['active', 'shadow']),
        auc: learningEstimateSchema,
        top20Precision: learningEstimateSchema,
    })),
    labelsBySource: z.array(z.object({ source: z.enum(LABEL_SOURCES), n: count })),
    catalogGap: z.array(z.object({ vehicle: z.string(), companies: count, of: count })),
    labelTasks: z.array(z.object({
        taskId: z.string(),
        company: z.string(),
        domain: z.string(),
        region: z.string(),
        evidence: z.array(z.string()),
        blind: z.boolean(),
        fitScore: z.number().nullable(),
        tier: z.enum(FIT_TIERS).nullable(),
    })),
});
export const verifierQualitySchema = z.object({
    disagreement: z.array(z.object({
        rule: z.string(),
        n: count,
        agentVsReferee: learningEstimateSchema,
        agentVsSercan: learningEstimateSchema,
    })),
    auditFieldError: learningEstimateSchema,
    mustNotDeliver: count,
    auditedRecords: count,
    ece: learningEstimateSchema,
    brier: learningEstimateSchema,
    fixtures: z.array(z.object({ origin: z.enum(['design', 'correction', 'reverify', 'audit']), count, passed: count })),
    regression: z.object({ passed: count, total: count, holdoutPassed: count, holdoutTotal: count }),
    notLearned: count,
    gateways: z.array(z.object({ gateway: z.string(), delivered: count, bounced: count, okRate: learningEstimateSchema })),
});
export const mailLearningSchema = z.object({
    sendingEnabled: z.boolean(),
    experiments: z.array(z.object({
        experimentId: z.string(),
        variable: z.enum(['subject', 'opening', 'cta', 'length', 'send_time', 'cadence', 'followup_delay']),
        country: z.string(),
        step: count,
        status: z.enum(['running', 'decided', 'inconclusive']),
        weeks: count,
        maxWeeks: count,
        variants: z.array(z.object({
            code: z.string(),
            delivered: count,
            positiveReplies: count,
            rate: learningEstimateSchema,
            // v0.1.258 (additive): the arm's text, first mails, firms that clicked as a person and firms that replied.
            label: z.string().optional(),
            firstMails: count.optional(),
            clickedFirms: count.optional(),
            replies: count.optional(),
        })),
        pBetter: z.number().nullable(),
        expectedLoss: z.number().nullable(),
    })),
    editRate: learningEstimateSchema,
    denetciCatchRate: learningEstimateSchema,
    blindSample: z.object({ reviewed: count, flagged: count }),
    confusion: z.object({ labels: z.array(z.string()), counts: z.array(z.array(count)) }),
    autoStops: z.array(z.object({ at: instant, what: z.string(), why: z.string() })),
});
/** The reply desk's last days (v0.1.52): Sercan's verdicts on reply drafts and his own replies. */
export const replyDeskSchema = z.object({
    days: count,
    drafts: count,
    passedFirstTime: count,
    denetciRejected: count,
    approved: count,
    edited: count,
    rejected: count,
    rejectedByReason: z.array(z.object({ reason: rejectReasonCodeSchema, n: count })),
    ownReplies: count,
    pending: count,
    firstPassRate: learningEstimateSchema,
    editRate: learningEstimateSchema,
    recent: z
        .array(z.object({
        approvalId: z.string(),
        kind: z.enum(['edited', 'rejected']),
        at: instant,
        reason: rejectReasonCodeSchema.nullable(),
        note: z.string().nullable(),
        title: z.string(),
    }))
        .max(10),
});
export const staleJobSchema = z.object({
    job: z.string().min(1),
    lastRunAt: instant.nullable(),
    expectedEveryHours: z.number().positive(),
    enabled: z.boolean(),
});
export const dataHealthSchema = z.object({
    attributionCoverage: learningEstimateSchema,
    failedSearchShare: learningEstimateSchema,
    pendingRewards: count,
    unresolvedResults: count,
    staleJobs: z.array(staleJobSchema),
});
// ---- the whole view -------------------------------------------------------------
export const learningFlagsSchema = z.object({
    leadEngine: z.boolean(),
    leadVerifier: z.boolean(),
    outreachSending: z.boolean(),
    learningAutoapply: z.boolean(),
});
export const learningViewSchema = z.object({
    asOf: instant,
    /** ISO week of the report ("2026-W40"). */
    week: z.string().regex(/^\d{4}-W\d{2}$/),
    flags: learningFlagsSchema,
    scorecard: z.array(scorecardTileSchema).length(SCORECARD_IDS.length),
    changes: z.array(learningChangeSchema),
    batch: learningBatchSchema.nullable(),
    arms: z.array(armRowSchema),
    vocabulary: z.array(vocabularyLocaleSchema),
    cells: cellLearningSchema,
    fit: fitReviewSchema,
    verification: verifierQualitySchema,
    mail: mailLearningSchema,
    health: dataHealthSchema,
    replyDesk: replyDeskSchema,
});
/**
 * POST /api/v1/learning/changes/{id}/revert (v0.1.34): Sercan takes an automatic change back.
 * The reason goes to the change row; confirmedByHold says the 1.5 s hold completed, which the
 * server requires when the revert loosens the search (reopening a pruned arm).
 */
export const revertBodySchema = z.strictObject({
    reason: z.string().trim().min(3).max(600),
    confirmedByHold: z.boolean(),
});
export const revertResultSchema = z.strictObject({
    changeId: z.string(),
    revertedAt: instant,
});
/**
 * POST /api/v1/learning/batches/{id}/decide (v0.1.35): Sercan's decisions on the weekly
 * learning batch. payloadHash must match the batch's current open items (payload_changed
 * otherwise); a reject needs a reason code; approving anything needs the written reason and
 * the completed batch hold; an H3 item needs its own hold as well. Deferred items stay open.
 */
export const LEARNING_DECISIONS = ['approve', 'reject', 'defer'];
export const learningItemDecisionSchema = z.strictObject({
    proposalId: z.string().regex(/^[1-9]\d{0,17}$/),
    decision: z.enum(LEARNING_DECISIONS),
    reasonCode: rejectReasonCodeSchema.nullable(),
    itemHoldConfirmed: z.boolean(),
});
export const learningBatchDecisionBodySchema = z.strictObject({
    payloadHash: z.string().min(4).max(128),
    decisions: z.array(learningItemDecisionSchema).max(50),
    reason: z.string().trim().max(600).nullable(),
    confirmedByHold: z.boolean(),
});
export const learningBatchResultSchema = z.strictObject({
    batchId: z.string(),
    decidedAt: instant,
    approved: count,
    rejected: count,
    deferred: count,
});
/** POST /api/v1/learning/fit-labels (v0.1.33): Sercan's label for one label task (the lead id). */
export const fitLabelBodySchema = z.strictObject({
    taskId: z.string().regex(/^[1-9]\d{0,17}$/),
    label: z.enum(FIT_LABELS),
    /** The score was hidden on the screen when the label was given. */
    blind: z.boolean(),
});
export const fitLabelResponseSchema = z.strictObject({
    leadId: z.string(),
    label: z.enum(FIT_LABELS),
    source: z.literal('sercan'),
});
/** ISO 8601 week label of a calendar date given as year, month (1-12) and day. */
export function isoWeekLabel(year, month, day) {
    const date = new Date(Date.UTC(year, month - 1, day));
    const weekday = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - weekday);
    const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1);
    const week = Math.ceil(((date.getTime() - yearStart) / 86_400_000 + 1) / 7);
    return `${date.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
/** The view before any learning data: every loop empty, every tile without an estimate. */
export function emptyLearningView(input) {
    return {
        asOf: input.asOf,
        week: input.week,
        flags: input.flags,
        scorecard: SCORECARD_TILES.map((tile) => ({
            ...tile,
            estimate: noEstimate(SCORECARD_MIN_N[tile.id]),
            trend: [null, null, null, null],
        })),
        changes: [],
        batch: null,
        arms: [],
        vocabulary: [],
        cells: {
            saturation: [],
            frontier: [],
            holdoutYield: noEstimate(30),
            frontierYield: noEstimate(30),
            regionEstimates: [],
            sectors: [],
        },
        fit: {
            reportOnly: true,
            formulaDecisionPending: true,
            tierOutcome: FIT_TIERS.map((tier) => ({
                tier,
                counts: { high: 0, medium: 0, low: 0, unfit: 0 },
                good: noEstimate(20),
            })),
            components: [],
            versions: [],
            labelsBySource: LABEL_SOURCES.map((source) => ({ source, n: 0 })),
            catalogGap: [],
            labelTasks: [],
        },
        verification: {
            disagreement: [],
            auditFieldError: noEstimate(20),
            mustNotDeliver: 0,
            auditedRecords: 0,
            ece: noEstimate(50),
            brier: noEstimate(50),
            fixtures: [],
            regression: { passed: 0, total: 0, holdoutPassed: 0, holdoutTotal: 0 },
            notLearned: 0,
            gateways: [],
        },
        mail: {
            sendingEnabled: input.flags.outreachSending,
            experiments: [],
            editRate: noEstimate(10),
            denetciCatchRate: noEstimate(20),
            blindSample: { reviewed: 0, flagged: 0 },
            confusion: { labels: [], counts: [] },
            autoStops: [],
        },
        health: {
            attributionCoverage: noEstimate(20),
            failedSearchShare: noEstimate(10, 'wilson'),
            pendingRewards: 0,
            unresolvedResults: 0,
            staleJobs: [],
        },
        replyDesk: {
            days: 7,
            drafts: 0,
            passedFirstTime: 0,
            denetciRejected: 0,
            approved: 0,
            edited: 0,
            rejected: 0,
            rejectedByReason: [],
            ownReplies: 0,
            pending: 0,
            firstPassRate: noEstimate(5, 'wilson'),
            editRate: noEstimate(5, 'wilson'),
            recent: [],
        },
    };
}
//# sourceMappingURL=learning.js.map